import { useState } from 'react';
import { Users, BookOpen, GraduationCap, Award, Brain, CheckCircle, ArrowRight, Star } from 'lucide-react';

function HomePage() {
  const [isDarkMode, setIsDarkMode] = useState(false);

  const scrollToSection = (sectionId: string) => {
    if (!import.meta.env.SSR && typeof document !== 'undefined') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const features = [
    { icon: BookOpen, title: 'Comprehensive Study Materials', description: 'Access detailed agricultural study materials crafted by industry experts covering all major subjects and topics' },
    { icon: Brain, title: 'Smart Practice Tests', description: 'Adaptive testing system that evolves with your learning progress and identifies your weak areas' },
    { icon: Users, title: 'Community Learning', description: 'Join a thriving community of agricultural students and professionals for collaborative learning' },
    { icon: Award, title: 'Certified Content', description: 'All materials verified by leading agricultural institutions and updated regularly' }
  ];

  const stats = [
    { number: '50K+', label: 'Active Students' },
    { number: '1000+', label: 'Practice Tests' },
    { number: '95%', label: 'Success Rate' },
    { number: '24/7', label: 'Expert Support' }
  ];

  const examTypes = [
    'JRF (Junior Research Fellowship)',
    'SRF (Senior Research Fellowship)', 
    'NET (National Eligibility Test)',
    'ICAR PG Entrance Exam',
    'State Agricultural University Exams',
    'Agricultural Officer Exams',
    'Bank PO Agriculture Specialist',
    'UPSC Agriculture Optional'
  ];

  const subjects = [
    'Agronomy', 'Horticulture', 'Plant Breeding', 'Soil Science',
    'Agricultural Economics', 'Agricultural Extension', 'Plant Pathology',
    'Entomology', 'Agricultural Biotechnology', 'Animal Husbandry'
  ];

  return (
    <div className={`min-h-screen ${isDarkMode ? 'dark' : ''}`}>
      <div className="bg-white dark:bg-gray-900 transition-colors duration-200">

        {/* Hero Section */}
        <section id="home" className="relative min-h-screen flex items-center">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-green-900/90 to-gray-900/90" />
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                  Master Agricultural<br />
                  <span className="text-green-400">Sciences</span> With<br />
                  Confidence
                </h1>
                <p className="text-xl text-gray-200 max-w-3xl mb-8">
                  At AgriMocks, we specialize in providing the best Agriculture Test Series tailored for Agriculture students to help them ace their exam goals. Our comprehensive and expertly designed test series covers All Agriculture Competitive and Entrance Exams, ensuring you're fully prepared for success.
                </p>
                <div className="flex flex-wrap gap-4">
                  <button onClick={() => scrollToSection('products')} className="bg-green-800 hover:bg-green-900 text-white px-8 py-3 rounded-[24px] text-lg font-semibold transition-colors duration-200">
                    Explore Products
                  </button>
                  <button onClick={() => scrollToSection('features')} className="bg-white/10 hover:bg-white/20 text-white px-8 py-3 rounded-[24px] text-lg font-semibold transition-colors duration-200 backdrop-blur-sm" aria-label="Learn More">
                    Learn More
                  </button>
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="relative">
                  <div className="absolute inset-0 bg-green-600/20 rounded-full filter blur-3xl animate-pulse" />
                  <div className="relative bg-white/10 backdrop-blur-lg rounded-[24px] p-8 border border-white/20">
                    <div className="flex items-center space-x-4 mb-6">
                      <GraduationCap className="h-12 w-12 text-green-400" />
                      <div>
                        <h2 className="text-xl font-semibold text-white">Start Learning Today</h2>
                        <p className="text-gray-300">Join 50,000+ agricultural students</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      {[
                        'Access to 1000+ practice tests',
                        'Expert-verified study materials',
                        'Interactive learning modules',
                        'Progress tracking & analytics'
                      ].map((feature, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <div className="h-2 w-2 rounded-full bg-green-400" />
                          <span className="text-gray-200">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="relative -mt-20 mb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 rounded-[24px] shadow-lg p-6 transform hover:-translate-y-1 transition-transform duration-300">
                  <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">{stat.number}</div>
                  <div className="text-gray-600 dark:text-gray-300">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-20 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Why Choose AgriMocks?</h2>
              <p className="text-xl text-gray-600 dark:text-gray-300">Empowering agricultural education with cutting-edge technology and expert guidance</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="bg-white dark:bg-gray-900 rounded-[24px] p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <div className="h-12 w-12 bg-green-100 dark:bg-green-900/50 rounded-[24px] flex items-center justify-center mb-6">
                      <Icon className="h-6 w-6 text-green-600 dark:text-green-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">{feature.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Subjects Section */}
        <section className="py-20 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Core Agricultural Subjects</h2>
              <p className="text-xl text-gray-600 dark:text-gray-300">Master all essential subjects with our comprehensive study materials and practice tests</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
              {subjects.map((subject, index) => (
                <div key={index} className="bg-white dark:bg-gray-900 rounded-lg p-4 text-center shadow-sm hover:shadow-md transition-shadow duration-200">
                  <span className="text-gray-700 dark:text-gray-300 font-medium">{subject}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section id="testimonials" className="py-20 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Student Success Stories</h2>
              <p className="text-xl text-gray-600 dark:text-gray-300">Hear from our community of agricultural professionals who achieved their goals</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  name: 'Sarah Johnson',
                  role: 'Agricultural Science Student',
                  image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80',
                  quote: 'AgriMocks helped me ace my agricultural science exams. The practice tests are incredibly detailed and relevant to the actual exam pattern.',
                  rating: 5
                },
                {
                  name: 'Michael Chen',
                  role: 'Farm Management Professional',
                  image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80',
                  quote: 'The study materials are comprehensive and up-to-date with the latest agricultural practices and technologies. Highly recommended!',
                  rating: 5
                },
                {
                  name: 'Emily Rodriguez',
                  role: 'Sustainable Farming Consultant',
                  image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80',
                  quote: 'Thanks to AgriMocks, I was able to transition into sustainable farming consulting with confidence. The platform is excellent.',
                  rating: 5
                }
              ].map((testimonial, index) => (
                <div key={index} className="bg-gray-50 dark:bg-gray-800 rounded-[24px] p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className="flex items-center space-x-4 mb-6">
                    <img src={testimonial.image} alt={testimonial.name} className="h-16 w-16 rounded-full object-cover" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{testimonial.name}</h3>
                      <p className="text-gray-600 dark:text-gray-400">{testimonial.role}</p>
                      <div className="flex items-center space-x-1 mt-1">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 italic">"{testimonial.quote}"</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        
      </div>
    </div>
  );
}

export default HomePage;